import React, { useState } from "react";
import { 
  Box, 
  Text, 
  VStack, 
  Card, 
  CardBody, 
  Heading,
  Center,
  List,
  ListItem,
  ListIcon,
  IconButton,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Code,
  Badge,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  HStack,
} from "@chakra-ui/react";
import { MdCheckCircle, MdBugReport, MdLightbulb, MdWarning, MdStar } from "react-icons/md";
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import ReactMarkdown from 'react-markdown';

export default function UNTUCKitAnalysisView({ conversationData }) {
  const analysisData = conversationData?.analysis;
  const rawData = conversationData;
  const [isDebugOpen, setIsDebugOpen] = useState(false);
  const [showSegments, setShowSegments] = useState(false);

  // Phase display names
  const phaseNames = {
    get_engaged: "GET ENGAGED",
    get_myfit: "GET MYFIT",
    give_experience: "GIVE THE EXPERIENCE",
    get_them_back: "GET THEM BACK"
  };

  // Phase colors
  const phaseColors = {
    get_engaged: "blue",
    get_myfit: "purple",
    give_experience: "green",
    get_them_back: "orange"
  };

  // Helper function to get score color
  const getScoreColor = (score) => {
    if (score >= 2.5) return "green";
    if (score >= 2) return "orange";
    return "red";
  };

  // Helper function to get score badge text
  const getScoreBadge = (score) => {
    if (score >= 2.5) return "Excellent";
    if (score >= 2) return "Good";
    if (score >= 1) return "Fair";
    return "Needs Work";
  };

  // Helper function to extract transcript text
  const getTranscriptText = (transcriptData) => {
    if (!transcriptData) return '';
    if (transcriptData.transcription) return transcriptData.transcription;
    if (transcriptData.result) return transcriptData.result;
    return '';
  };

  // Debug drawer component
  const DebugDrawer = () => (
    <Drawer
      isOpen={isDebugOpen}
      placement="right"
      onClose={() => setIsDebugOpen(false)}
      size="lg"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader>Debug Information</DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Card>
              <CardBody>
                <Heading size="sm" mb={2}>Raw Conversation Data</Heading>
                <Button
                  size="sm"
                  leftIcon={showSegments ? <ChevronDownIcon /> : <ChevronRightIcon />}
                  onClick={() => setShowSegments(!showSegments)}
                  variant="ghost"
                  mb={2}
                >
                  {showSegments ? 'Hide' : 'Show'} Full Data
                </Button>
                <Code display="block" whiteSpace="pre" overflow="auto" p={2}>
                  {JSON.stringify(showSegments ? rawData : analysisData, null, 2)}
                </Code>
              </CardBody>
            </Card>

            {rawData?.raw_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>Raw Transcript</Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.raw_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}

            {rawData?.filtered_transcript_data && (
              <Card>
                <CardBody>
                  <Heading size="sm" mb={2}>Filtered Transcript</Heading>
                  <Text
                    whiteSpace="pre-wrap"
                    p={2}
                    bg="gray.50"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    {getTranscriptText(rawData.filtered_transcript_data)}
                  </Text>
                </CardBody>
              </Card>
            )}
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  if (!analysisData || !analysisData.segments || !analysisData.section_feedback) {
    return (
      <Center h="100vh">
        <Text>No UNTUCKit analysis data available</Text>
      </Center>
    );
  }

  return (
    <Box p={5} maxW="1200px" mx="auto" position="relative">
      {/* Debug Button */}
      <IconButton
        icon={<MdBugReport />}
        position="fixed"
        right="4"
        top="4"
        colorScheme="gray"
        onClick={() => setIsDebugOpen(true)}
        aria-label="Debug View"
        title="Open Debug View"
      />

      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading mb={2}>UNTUCKit Sales Analysis</Heading>
          <Text color="gray.600">Performance evaluation based on the UNTUCKit framework</Text>
        </Box>

        {/* Overall Score Summary */}
        <Card borderWidth="2px" borderColor="blue.200" bg="blue.50">
          <CardBody>
            <Heading size="md" mb={4}>Overall Performance</Heading>
            <HStack spacing={8} wrap="wrap">
              {analysisData.section_feedback.map((section, index) => {
                const phaseKey = Object.keys(phaseNames)[index];
                return (
                  <VStack key={section.phase} align="center" spacing={2}>
                    <Text fontWeight="bold" fontSize="sm">{section.phase}</Text>
                    <Box width="100px" height="24px" bg="gray.200" borderRadius="md" overflow="hidden">
                      <Box
                        width={`${(section.score / 3) * 100}%`}
                        height="100%"
                        bg={
                          section.score >= 2.5 ? "green.500" :
                          section.score >= 2 ? "orange.400" :
                          "red.500"
                        }
                        transition="width 0.3s ease"
                      />
                    </Box>
                    <Badge colorScheme={getScoreColor(section.score)}>
                      {section.score}/3
                    </Badge>
                  </VStack>
                );
              })}
            </HStack>
          </CardBody>
        </Card>

        {/* Phase Details */}
        <Accordion allowMultiple defaultIndex={[0]}>
          {analysisData.section_feedback.map((section, index) => {
            const phaseKey = Object.keys(phaseNames)[index];
            const phaseSegments = analysisData.segments[phaseKey] || [];
            
            return (
              <AccordionItem key={section.phase}>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left">
                      <HStack>
                        <Heading size="md">{section.phase}</Heading>
                        <Badge 
                          colorScheme={getScoreColor(section.score)} 
                          fontSize="md"
                          px={3}
                          py={1}
                        >
                          {section.score}/3 - {getScoreBadge(section.score)}
                        </Badge>
                      </HStack>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <VStack align="stretch" spacing={4}>
                    {/* Conversation Segments */}
                    {phaseSegments.length > 0 && (
                      <Card bg="gray.50">
                        <CardBody>
                          <Heading size="sm" mb={3}>Conversation Excerpts</Heading>
                          <VStack align="stretch" spacing={2}>
                            {phaseSegments.map((segment, idx) => (
                              <Text 
                                key={idx} 
                                fontSize="sm" 
                                fontStyle="italic"
                                p={2}
                                bg="white"
                                borderRadius="md"
                                borderLeft="3px solid"
                                borderLeftColor={`${phaseColors[phaseKey]}.400`}
                              >
                                "{segment}"
                              </Text>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>
                    )}

                    {/* Strengths */}
                    {section.strengths.length > 0 && (
                      <Card borderColor="green.200" borderWidth="1px">
                        <CardBody>
                          <HStack mb={3}>
                            <MdStar color="green" />
                            <Heading size="sm">Strengths</Heading>
                          </HStack>
                          <List spacing={2}>
                            {section.strengths.map((strength, idx) => (
                              <ListItem key={idx}>
                                <ListIcon as={MdCheckCircle} color="green.500" />
                                <Text as="span" fontSize="sm">{strength}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </CardBody>
                      </Card>
                    )}

                    {/* Opportunities */}
                    {section.opportunities.length > 0 && (
                      <Card borderColor="orange.200" borderWidth="1px">
                        <CardBody>
                          <HStack mb={3}>
                            <MdWarning color="orange" />
                            <Heading size="sm">Opportunities for Growth</Heading>
                          </HStack>
                          <List spacing={2}>
                            {section.opportunities.map((opportunity, idx) => (
                              <ListItem key={idx}>
                                <ListIcon as={MdLightbulb} color="orange.500" />
                                <Text as="span" fontSize="sm">{opportunity}</Text>
                              </ListItem>
                            ))}
                          </List>
                        </CardBody>
                      </Card>
                    )}
                    {/* Micro Drill */}
                    {section.micro_drill && (
                      <Card bg="purple.50" borderColor="purple.200" borderWidth="1px">
                        <CardBody>
                          <HStack mb={2}>
                            <Text fontSize="lg">🎯</Text>
                            <Heading size="sm">Practice Focus</Heading>
                          </HStack>
                          <Text fontSize="sm" fontWeight="medium">
                            {section.micro_drill}
                          </Text>
                        </CardBody>
                      </Card>
                    )}                    
                  </VStack>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      </VStack>

      {/* Debug Drawer */}
      <DebugDrawer />
    </Box>
  );
}